import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button, Card, CardHeader, Money, StatusPill } from "@/components/ui";
import { ApiError } from "@/lib/api";
import {
  closeCashSession,
  currentCashTotals,
  openCashSession,
  registerCashMovement,
  type CashTotalsPublic,
} from "@/lib/endpoints";
import { getSession } from "@/lib/session";

const REGISTER = "CAJA-1";

/** Billetes y monedas de la zona euro-peso mexicano, de mayor a menor. */
const DENOMINATIONS = [1000_00, 500_00, 200_00, 100_00, 50_00, 20_00, 10_00, 5_00, 2_00, 1_00];

/**
 * Pantalla de caja.
 *
 * Cubre el ciclo completo del turno: apertura con fondo, corte X en cualquier
 * momento, retiros e ingresos, y cierre con **arqueo por denominación**, que es
 * lo que permite explicar un faltante en lugar de sólo detectarlo.
 */
export function CashScreen() {
  const session = getSession();
  const branch = session?.branches[0] ?? null;
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const totals = useQuery<CashTotalsPublic | null>({
    queryKey: ["cash", "current"],
    queryFn: async () => {
      try {
        return await currentCashTotals();
      } catch (cause) {
        if (cause instanceof ApiError && cause.status === 404) return null;
        throw cause;
      }
    },
  });

  const open = useMutation({
    mutationFn: (floatCents: number) =>
      openCashSession({
        branch_id: branch?.id ?? "",
        register_code: REGISTER,
        opening_float_cents: floatCents,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["cash"] }),
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "No se pudo abrir el turno"),
  });

  if (totals.isPending) {
    return <p className="p-6 text-muted">Cargando el turno…</p>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-6">
      {error ? (
        <p role="alert" className="rounded-msr border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {totals.data ? (
        <OpenSessionView totals={totals.data} onError={setError} />
      ) : (
        <Card>
          <CardHeader
            title="Abrir turno de caja"
            description="Sin turno abierto no se puede cobrar: no habría forma de cuadrar el efectivo."
          />
          <OpenForm onSubmit={(cents) => open.mutate(cents)} loading={open.isPending} />
        </Card>
      )}
    </div>
  );
}

function OpenForm({
  onSubmit,
  loading,
}: {
  onSubmit: (cents: number) => void;
  loading: boolean;
}) {
  const [float, setFloat] = useState("0");

  return (
    <form
      className="flex flex-wrap items-end gap-3 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(Math.round(Number.parseFloat(float.replace(",", ".") || "0") * 100));
      }}
    >
      <label className="block">
        <span className="mb-1 block text-xs tracking-wide text-muted uppercase">
          Fondo inicial
        </span>
        <input
          value={float}
          onChange={(event) => setFloat(event.target.value)}
          inputMode="decimal"
          className="focus:border-electric h-11 w-40 rounded-msr border border-line bg-snow px-3 text-right text-lg text-carbon focus:outline-none"
        />
      </label>
      <Button type="submit" size="lg" loading={loading}>
        Abrir turno
      </Button>
      <p className="text-xs text-muted">
        El fondo inicial queda registrado como el primer movimiento del cajón.
      </p>
    </form>
  );
}

function OpenSessionView({
  totals,
  onError,
}: {
  totals: CashTotalsPublic;
  onError: (message: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [movementKind, setMovementKind] = useState<"retiro" | "ingreso">("retiro");
  const [movementAmount, setMovementAmount] = useState("");
  const [movementReason, setMovementReason] = useState("");
  const [closing, setClosing] = useState(false);

  const movement = useMutation({
    mutationFn: () =>
      registerCashMovement({
        cash_session_id: totals.cash_session_id,
        kind: movementKind,
        amount_cents: Math.round(Number.parseFloat(movementAmount.replace(",", ".") || "0") * 100),
        reason: movementReason,
      }),
    onSuccess: () => {
      setMovementAmount("");
      setMovementReason("");
      void queryClient.invalidateQueries({ queryKey: ["cash"] });
    },
    onError: (cause) =>
      onError(cause instanceof ApiError ? cause.message : "No se pudo registrar el movimiento"),
  });

  return (
    <>
      <Card>
        <CardHeader
          title={`Turno abierto · ${totals.register_code}`}
          description={`Día operativo ${totals.business_date} · ${totals.movement_count} movimientos`}
          actions={<StatusPill tone="ok">Abierto</StatusPill>}
        />
        <div className="grid gap-4 p-4 sm:grid-cols-3">
          <Stat label="Efectivo esperado" cents={totals.expected_cash_cents} highlight />
          <Stat label="Ventas del turno" value={String(totals.sales_count)} />
          <Stat label="Propinas" cents={totals.tips_cents} />
        </div>

        <div className="border-t border-line p-4">
          <h3 className="font-display mb-3 text-sm font-bold text-carbon">Cobrado por método</h3>
          <ul className="space-y-1 text-sm">
            {Object.entries(totals.sales_by_method ?? {}).length === 0 ? (
              <li className="text-muted">Todavía no hay ventas en este turno.</li>
            ) : (
              Object.entries(totals.sales_by_method ?? {}).map(([method, cents]) => (
                <li key={method} className="flex justify-between capitalize">
                  <span className="text-muted">{method}</span>
                  <Money cents={cents} size="sm" />
                </li>
              ))
            )}
          </ul>
          <p className="mt-3 text-xs text-muted">
            Sólo el efectivo está en el cajón; el resto se cobró por otros medios.
          </p>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Retiro o ingreso de efectivo"
          description="Siempre con motivo: un retiro sin explicación es un descuadre con pasos extra."
        />
        <form
          className="flex flex-wrap items-end gap-3 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            onError(null);
            movement.mutate();
          }}
        >
          <label className="block">
            <span className="mb-1 block text-xs text-muted uppercase">Tipo</span>
            <select
              value={movementKind}
              onChange={(event) => setMovementKind(event.target.value as "retiro" | "ingreso")}
              className="focus:border-electric h-11 rounded-msr border border-line bg-snow px-3 text-carbon focus:outline-none"
            >
              <option value="retiro">Retiro</option>
              <option value="ingreso">Ingreso</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted uppercase">Importe</span>
            <input
              value={movementAmount}
              onChange={(event) => setMovementAmount(event.target.value)}
              inputMode="decimal"
              required
              className="focus:border-electric h-11 w-36 rounded-msr border border-line bg-snow px-3 text-right text-carbon focus:outline-none"
            />
          </label>
          <label className="min-w-56 flex-1">
            <span className="mb-1 block text-xs text-muted uppercase">Motivo</span>
            <input
              value={movementReason}
              onChange={(event) => setMovementReason(event.target.value)}
              required
              minLength={3}
              className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow px-3 text-carbon focus:outline-none"
            />
          </label>
          <Button type="submit" loading={movement.isPending}>
            Registrar
          </Button>
        </form>
      </Card>

      <Card>
        <CardHeader
          title="Cerrar turno"
          description="El arqueo congela el corte: si mañana se anula algo, este corte no cambia."
        />
        <div className="p-4">
          {closing ? (
            <CloseForm
              cashSessionId={totals.cash_session_id}
              expectedCents={totals.expected_cash_cents}
              onDone={() => {
                setClosing(false);
                void queryClient.invalidateQueries({ queryKey: ["cash"] });
              }}
            />
          ) : (
            <Button variant="secondary" size="lg" onClick={() => setClosing(true)}>
              Hacer corte Z
            </Button>
          )}
        </div>
      </Card>
    </>
  );
}

function CloseForm({
  cashSessionId,
  expectedCents,
  onDone,
}: {
  cashSessionId: string;
  expectedCents: number;
  onDone: () => void;
}) {
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const counted = DENOMINATIONS.reduce(
    (sum, denomination) => sum + denomination * (counts[denomination] ?? 0),
    0,
  );
  const difference = counted - expectedCents;

  const close = useMutation({
    mutationFn: () =>
      closeCashSession(cashSessionId, {
        counted_cash_cents: counted,
        denominations: DENOMINATIONS.filter((value) => (counts[value] ?? 0) > 0).map((value) => ({
          denomination_cents: value,
          quantity: counts[value] ?? 0,
        })),
        notes: notes || null,
      }),
    onSuccess: onDone,
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : "No se pudo cerrar el turno"),
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        close.mutate();
      }}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {DENOMINATIONS.map((denomination) => (
          <label key={denomination} className="flex items-center gap-2">
            <span className="w-20 text-right text-sm text-muted">
              <Money cents={denomination} size="sm" />
            </span>
            <input
              type="number"
              min={0}
              value={counts[denomination] ?? ""}
              onChange={(event) =>
                setCounts((current) => ({
                  ...current,
                  [denomination]: Math.max(0, Number.parseInt(event.target.value || "0", 10)),
                }))
              }
              className="focus:border-electric tabular h-10 w-20 rounded-msr border border-line bg-snow px-2 text-right text-carbon focus:outline-none"
            />
          </label>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Esperado" cents={expectedCents} />
        <Stat label="Contado" cents={counted} />
        <Stat
          label="Diferencia"
          cents={difference}
          tone={difference === 0 ? "ok" : difference < 0 ? "danger" : "warning"}
        />
      </div>

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <label className="block">
        <span className="mb-1 block text-xs text-muted uppercase">Notas del cierre</span>
        <input
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Sin novedad"
          className="focus:border-electric h-11 w-full rounded-msr border border-line bg-snow px-3 text-carbon placeholder:text-muted focus:outline-none"
        />
      </label>

      <p className="text-xs text-muted">
        Una diferencia no bloquea el cierre: el dinero ya se contó y ocultarlo no lo arregla.
        Queda registrada con su desglose.
      </p>

      <Button type="submit" size="lg" loading={close.isPending}>
        Cerrar turno
      </Button>
    </form>
  );
}

function Stat({
  label,
  cents,
  value,
  highlight,
  tone,
}: {
  label: string;
  cents?: number;
  value?: string;
  highlight?: boolean;
  tone?: "ok" | "danger" | "warning";
}) {
  const color =
    tone === "ok"
      ? "text-success"
      : tone === "danger"
        ? "text-danger"
        : tone === "warning"
          ? "text-warning"
          : highlight
            ? "text-electric font-black"
            : "text-carbon";

  return (
    <div className="rounded-msr border border-line bg-surface/50 p-3">
      <p className="text-xs tracking-wide text-muted uppercase">{label}</p>
      <p className={`mt-1 text-xl font-bold ${color}`}>
        {cents !== undefined ? <Money cents={cents} size="lg" /> : value}
      </p>
    </div>
  );
}
